/**
 * Segment Effects Inspector Component
 * 
 * Bottom panel inspector showing only segment effects (zoom, pan, speed, transitions).
 * Range and metadata controls are handled by the Timeline component.
 * 
 * NOTE: Segment effects are NOT part of Phase 1. All controls are disabled but visible.
 */

import React from 'react';
import { EditorClip } from '../types';

interface SegmentEffectsInspectorProps {
  selectedClip: EditorClip | null;
  onDelete: () => void;
  segmentEffectsConfig?: any;
  onSegmentEffectsChange?: (config: any) => void;
  disabled?: boolean; // Phase 1: Disable all segment effects
}

const SegmentEffectsInspector: React.FC<SegmentEffectsInspectorProps> = React.memo(({
  selectedClip,
  onDelete,
  segmentEffectsConfig,
  onSegmentEffectsChange,
  disabled = true, // Phase 1: Disabled by default
}) => {
  if (!selectedClip) {
    return null;
  }

  const segmentId = selectedClip.id;

  // Get effects for this specific segment
  const segmentEffects = segmentEffectsConfig?.segments?.find(
    (s: any) => s.segmentId === segmentId
  )?.effects || {};

  return (
    <div className="flex flex-col bg-white">
      {/* Header */}
      <div className="px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-xs font-medium text-gray-700">
              {selectedClip.label || 'Selected Segment'}
            </h3>
            {selectedClip.metadata?.matchMinute !== undefined && (
              <span className="text-xs text-gray-500">{selectedClip.metadata.matchMinute}'</span>
            )}
          </div>
          <button
            onClick={onDelete}
            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete segment"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content - No scroll, expands naturally */}
      <div className="p-3 space-y-3">
        {/* Phase 1 Notice */}
        {disabled && (
          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            ⚠️ Segment effects (Zoom, Speed, Transitions) are not part of Phase 1 and are disabled.
          </div>
        )}
        
        {/* Segment Effects */}
        <div>
          <label className="block text-xs text-gray-600 mb-2">
            Effects
          </label>
          <div className="space-y-3">
            {/* Zoom Effect */}
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <label className={`flex items-center space-x-2 mb-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={segmentEffects.zoom?.enabled || false}
                  disabled={disabled}
                  onChange={(e) => {
                    if (onSegmentEffectsChange) {
                      const newConfig = {
                        ...segmentEffectsConfig,
                        segments: [
                          ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                          {
                            segmentId,
                            effects: {
                              ...segmentEffects,
                              zoom: {
                                ...segmentEffects.zoom,
                                enabled: e.target.checked,
                                type: segmentEffects.zoom?.type || 'ken-burns',
                                startScale: segmentEffects.zoom?.startScale || 1.0,
                                endScale: segmentEffects.zoom?.endScale || 1.3,
                                panDirection: segmentEffects.zoom?.panDirection || 'center',
                              },
                            },
                          },
                        ],
                      };
                      onSegmentEffectsChange(newConfig);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">Zoom</span>
              </label>
              
              {segmentEffects.zoom?.enabled && (
                <div className="mt-2 space-y-2 pl-6">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Type</label>
                    <select
                      value={segmentEffects.zoom.type || 'ken-burns'}
                      disabled={disabled}
                      onChange={(e) => {
                        if (onSegmentEffectsChange) {
                          const newConfig = {
                            ...segmentEffectsConfig,
                            segments: [
                              ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                              {
                                segmentId,
                                effects: {
                                  ...segmentEffects,
                                  zoom: { ...segmentEffects.zoom, type: e.target.value },
                                },
                              },
                            ],
                          };
                          onSegmentEffectsChange(newConfig);
                        }
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="ken-burns">Ken Burns</option>
                      <option value="static-zoom">Static Zoom</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Scale: {segmentEffects.zoom.startScale || 1.0} - {segmentEffects.zoom.endScale || 1.3}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="range"
                        min="1.0"
                        max="2.0"
                        step="0.1"
                        value={segmentEffects.zoom.startScale || 1.0}
                        disabled={disabled}
                        onChange={(e) => {
                          if (onSegmentEffectsChange) {
                            const newConfig = {
                              ...segmentEffectsConfig,
                              segments: [
                                ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                                {
                                  segmentId,
                                  effects: {
                                    ...segmentEffects,
                                    zoom: { ...segmentEffects.zoom, startScale: parseFloat(e.target.value) },
                                  },
                                },
                              ],
                            };
                            onSegmentEffectsChange(newConfig);
                          }
                        }}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <span className="text-xs text-gray-500 w-8">Start</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="range"
                        min="1.0"
                        max="2.0"
                        step="0.1"
                        value={segmentEffects.zoom.endScale || 1.3}
                        disabled={disabled}
                        onChange={(e) => {
                          if (onSegmentEffectsChange) {
                            const newConfig = {
                              ...segmentEffectsConfig,
                              segments: [
                                ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                                {
                                  segmentId,
                                  effects: {
                                    ...segmentEffects,
                                    zoom: { ...segmentEffects.zoom, endScale: parseFloat(e.target.value) },
                                  },
                                },
                              ],
                            };
                            onSegmentEffectsChange(newConfig);
                          }
                        }}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <span className="text-xs text-gray-500 w-8">End</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Pan Direction</label>
                    <select
                      value={segmentEffects.zoom.panDirection || 'center'}
                      disabled={disabled}
                      onChange={(e) => {
                        if (onSegmentEffectsChange) {
                          const newConfig = {
                            ...segmentEffectsConfig,
                            segments: [
                              ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                              {
                                segmentId,
                                effects: {
                                  ...segmentEffects,
                                  zoom: { ...segmentEffects.zoom, panDirection: e.target.value },
                                },
                              },
                            ],
                          };
                          onSegmentEffectsChange(newConfig);
                        }
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="center">Center</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Speed Effect */}
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <label className={`flex items-center space-x-2 mb-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={segmentEffects.speed !== undefined && segmentEffects.speed !== 1.0}
                  disabled={disabled}
                  onChange={(e) => {
                    if (onSegmentEffectsChange) {
                      const newConfig = {
                        ...segmentEffectsConfig,
                        segments: [
                          ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                          {
                            segmentId,
                            effects: {
                              ...segmentEffects,
                              speed: e.target.checked ? 0.5 : 1.0,
                            },
                          },
                        ],
                      };
                      onSegmentEffectsChange(newConfig);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">Speed</span>
              </label>
              
              {segmentEffects.speed !== undefined && segmentEffects.speed !== 1.0 && (
                <div className="mt-2 pl-6">
                  <label className="block text-xs text-gray-600 mb-1">
                    Speed: {segmentEffects.speed}x
                  </label>
                  <input
                    type="range"
                    min="0.25"
                    max="2.0"
                    step="0.25"
                    value={segmentEffects.speed}
                    disabled={disabled}
                    onChange={(e) => {
                      if (onSegmentEffectsChange) {
                        const newConfig = {
                          ...segmentEffectsConfig,
                          segments: [
                            ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                            {
                              segmentId,
                              effects: {
                                ...segmentEffects,
                                speed: parseFloat(e.target.value),
                              },
                            },
                          ],
                        };
                        onSegmentEffectsChange(newConfig);
                      }
                    }}
                    className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0.25x</span>
                    <span>1.0x</span>
                    <span>2.0x</span>
                  </div>
                </div>
              )}
            </div>

            {/* Transitions */}
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <label className="block text-xs text-gray-700 mb-2">Transitions</label>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Transition In</label>
                  <select
                    value={segmentEffects.transitionIn || 'none'}
                    disabled={disabled}
                    onChange={(e) => {
                      if (onSegmentEffectsChange) {
                        const newConfig = {
                          ...segmentEffectsConfig,
                          segments: [
                            ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                            {
                              segmentId,
                              effects: {
                                ...segmentEffects,
                                transitionIn: e.target.value,
                              },
                            },
                          ],
                        };
                        onSegmentEffectsChange(newConfig);
                      }
                    }}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="fade">Fade</option>
                    <option value="crossfade">Crossfade</option>
                    <option value="slide">Slide</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Transition Out</label>
                  <select
                    value={segmentEffects.transitionOut || 'none'}
                    disabled={disabled}
                    onChange={(e) => {
                      if (onSegmentEffectsChange) {
                        const newConfig = {
                          ...segmentEffectsConfig,
                          segments: [
                            ...(segmentEffectsConfig?.segments || []).filter((s: any) => s.segmentId !== segmentId),
                            {
                              segmentId,
                              effects: {
                                ...segmentEffects,
                                transitionOut: e.target.value,
                              },
                            },
                          ],
                        };
                        onSegmentEffectsChange(newConfig);
                      }
                    }}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="fade">Fade</option>
                    <option value="crossfade">Crossfade</option>
                    <option value="slide">Slide</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SegmentEffectsInspector.displayName = 'SegmentEffectsInspector';

export default SegmentEffectsInspector;
